import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, ArrowRightLeft, BadgeCheck, CircleAlert, Database, LoaderCircle, RefreshCw, Smartphone, Users } from 'lucide-react';
import { getSessionMigrationSessions, migrateSelectedSessions, previewSessionMigration } from '../services/api';

type MigrationSession = {
  session_id: string;
  phone_number: string;
  status: string;
  is_ready: boolean;
  can_migrate: boolean;
  message_count: number;
  contact_count: number;
  migration_status: string;
  workspace_id?: number;
  migrated_at?: string;
  reason?: string;
};

type MigrationResult = {
  session_id: string;
  phone_number: string;
  success: boolean;
  message_count: number;
  contact_count: number;
  workspace_id?: number;
  error?: string;
};

type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
};

interface SessionMigrationWizardProps {
  toast?: ToastApi;
}

const numberFormat = new Intl.NumberFormat('es-PE');

const statusLabel = (status: string) => {
  if (status === 'complete') return 'Migrada';
  if (status === 'in_progress') return 'Pendiente de reintento';
  return 'Sin migrar';
};

const getErrorMessage = (error: any) => error?.response?.data?.error || error?.message || 'No se pudo completar la solicitud.';

export const SessionMigrationWizard: React.FC<SessionMigrationWizardProps> = ({ toast }) => {
  const [sessions, setSessions] = useState<MigrationSession[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<MigrationSession[] | null>(null);
  const [results, setResults] = useState<MigrationResult[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [loadError, setLoadError] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSessionMigrationSessions();
      const loadedSessions = (response.sessions || []) as MigrationSession[];
      setSessions(loadedSessions);
      setSelectedIds((current) => current.filter((id) => loadedSessions.some((session) => session.session_id === id && session.can_migrate && session.migration_status !== 'complete')));
      setPreview(null);
      setLoadError('');
    } catch (error: any) {
      setSessions([]);
      setLoadError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const selectedSessions = useMemo(
    () => sessions.filter((session) => selectedIds.includes(session.session_id)),
    [sessions, selectedIds],
  );

  const toggleSession = (session: MigrationSession) => {
    setSelectedIds((current) => current.includes(session.session_id)
      ? current.filter((id) => id !== session.session_id)
      : [...current, session.session_id]);
    setPreview(null);
    setResults(null);
  };

  const toggleAllPending = () => {
    const pendingIds = sessions
      .filter((session) => session.can_migrate && session.migration_status !== 'complete')
      .map((session) => session.session_id);
    const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : pendingIds);
    setPreview(null);
    setResults(null);
  };

  const handlePreview = async () => {
    if (selectedIds.length === 0) return;
    setPreviewing(true);
    setResults(null);
    try {
      const response = await previewSessionMigration(selectedIds);
      setPreview(response.sessions || []);
    } catch (error: any) {
      setPreview(null);
      toast?.error(getErrorMessage(error));
    } finally {
      setPreviewing(false);
    }
  };

  const handleMigrate = async () => {
    if (!preview || selectedIds.length === 0) return;
    setMigrating(true);
    try {
      const response = await migrateSelectedSessions(selectedIds);
      const migrationResults = (response.results || []) as MigrationResult[];
      setResults(migrationResults);
      setPreview(null);
      const completed = migrationResults.filter((result) => result.success).length;
      if (completed === migrationResults.length) {
        toast?.success(`${completed} sesión${completed === 1 ? '' : 'es'} migrada${completed === 1 ? '' : 's'}.`);
      } else {
        toast?.warning(`Se migraron ${completed} de ${migrationResults.length} sesiones. Puedes reintentar las pendientes.`);
      }
      await loadSessions();
      setSelectedIds((current) => current.filter((id) => migrationResults.some((result) => result.session_id === id && !result.success)));
    } catch (error: any) {
      toast?.error(getErrorMessage(error));
    } finally {
      setMigrating(false);
    }
  };

  const totalMessages = (preview || []).reduce((sum, session) => sum + session.message_count, 0);
  const totalContacts = (preview || []).reduce((sum, session) => sum + session.contact_count, 0);
  const allPendingSelected = sessions.filter((session) => session.can_migrate && session.migration_status !== 'complete').length > 0
    && sessions.filter((session) => session.can_migrate && session.migration_status !== 'complete').every((session) => selectedIds.includes(session.session_id));

  return (
    <div className="w-full min-w-0 space-y-6 pb-8">
      <section className="relative overflow-hidden rounded-3xl border border-theme bg-theme-card shadow-sm">
        <div className="absolute right-0 top-0 h-48 w-48 translate-x-14 -translate-y-20 rounded-full border-[24px] border-primary-500/10" />
        <div className="relative grid gap-6 p-6 sm:p-8 md:grid-cols-[minmax(0,1fr)_220px] md:items-end">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-amber-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              <Database size={14} /> Dev · copia de main disponible
            </div>
            <h3 className="max-w-2xl text-2xl font-bold tracking-tight text-theme-main sm:text-3xl">Organiza las conversaciones por número.</h3>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-theme-muted sm:text-base">
              Esta acción asocia los mensajes históricos de la copia de datos con un espacio por número. La base principal de producción no se modifica; la aplicación usa un único PostgreSQL con aislamiento lógico por espacio.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
            <div className="rounded-2xl border border-theme bg-theme-base/70 p-4">
              <div className="flex items-center gap-2 text-theme-muted"><Smartphone size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Sesiones</span></div>
              <p className="mt-2 text-2xl font-bold tabular-nums text-theme-main">{numberFormat.format(sessions.length)}</p>
            </div>
            <div className="rounded-2xl border border-theme bg-theme-base/70 p-4">
              <div className="flex items-center gap-2 text-theme-muted"><BadgeCheck size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Ya migradas</span></div>
              <p className="mt-2 text-2xl font-bold tabular-nums text-theme-main">{numberFormat.format(sessions.filter((session) => session.migration_status === 'complete').length)}</p>
            </div>
          </div>
        </div>
      </section>

      {loadError && (
        <section className="flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          <CircleAlert className="mt-0.5 shrink-0" size={19} />
          <div>
            <p className="font-semibold">La migración no está habilitada en este backend</p>
            <p className="mt-1 text-sm leading-5">{loadError}</p>
            <p className="mt-2 text-xs leading-5 opacity-80">Para probarla, habilita <code className="rounded bg-black/5 px-1 py-0.5 dark:bg-white/10">SESSION_WORKSPACE_MIGRATIONS_ENABLED=true</code> en el entorno local y reinicia el backend. La base de datos debe ser de desarrollo.</p>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-3xl border border-theme bg-theme-card shadow-sm">
        <div className="flex flex-col gap-4 border-b border-theme px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-700 dark:text-primary-300">Paso 1 · selección</p>
            <h4 className="mt-1 text-lg font-bold text-theme-main">Números de WhatsApp de tu cuenta</h4>
            <p className="mt-1 text-sm text-theme-muted">La cuenta propietaria y la sesión de WhatsApp siguen siendo independientes.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={toggleAllPending} disabled={loading || sessions.length === 0} className="rounded-xl border border-theme px-3 py-2 text-xs font-semibold text-theme-muted transition hover:bg-theme-base disabled:opacity-50">
              {allPendingSelected ? 'Quitar selección' : 'Seleccionar pendientes'}
            </button>
            <button type="button" onClick={() => void loadSessions()} disabled={loading || migrating} aria-label="Actualizar sesiones" className="rounded-xl border border-theme p-2 text-theme-muted transition hover:bg-theme-base disabled:opacity-50">
              {loading ? <LoaderCircle size={17} className="animate-spin" /> : <RefreshCw size={17} />}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-14 text-sm text-theme-muted"><LoaderCircle size={18} className="animate-spin" /> Cargando sesiones…</div>
        ) : loadError ? (
          <div className="px-6 py-10 text-center text-sm text-theme-muted">Cuando la migración esté habilitada, aquí aparecerán los números vinculados con QR.</div>
        ) : sessions.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Smartphone className="mx-auto mb-3 text-theme-muted" size={27} />
            <p className="font-semibold text-theme-main">No hay sesiones de WhatsApp en esta cuenta</p>
            <p className="mt-1 text-sm text-theme-muted">Vincula primero un número con el código QR.</p>
          </div>
        ) : (
          <div className="divide-y divide-theme">
            {sessions.map((session) => {
              const complete = session.migration_status === 'complete';
              const checked = selectedIds.includes(session.session_id);
              return (
                <label key={session.session_id} className={`grid cursor-pointer grid-cols-[22px_minmax(0,1fr)] gap-3 px-5 py-4 transition sm:grid-cols-[22px_minmax(0,1fr)_auto] sm:items-center sm:px-6 ${complete ? 'cursor-default bg-emerald-500/[0.025]' : 'hover:bg-theme-base/60'}`}>
                  <input type="checkbox" checked={checked} disabled={!session.can_migrate || complete || migrating} onChange={() => toggleSession(session)} className="mt-1 h-4 w-4 rounded border-theme text-primary-600 focus:ring-primary-500 sm:mt-0" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-theme-main">{session.phone_number ? `+${session.phone_number.replace(/^\+/, '')}` : 'Número sin vincular'}</span>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${complete ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' : session.migration_status === 'in_progress' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200' : 'bg-theme-base text-theme-muted'}`}>
                        {statusLabel(session.migration_status)}
                      </span>
                      <span className={`text-xs ${session.is_ready ? 'text-emerald-700 dark:text-emerald-300' : 'text-theme-muted'}`}>{session.is_ready ? 'Conectada' : 'Desconectada'}</span>
                    </div>
                    {session.reason ? <p className="mt-1 text-xs text-theme-muted">{session.reason}</p> : (
                      <p className="mt-1 truncate font-mono text-[11px] text-theme-muted">{session.session_id}</p>
                    )}
                  </div>
                  <div className="col-start-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-theme-muted sm:col-start-auto sm:justify-end">
                    {complete ? <>
                      <span className="inline-flex items-center gap-1.5"><ArrowRightLeft size={13} /> {numberFormat.format(session.message_count)} mensajes</span>
                      <span className="inline-flex items-center gap-1.5"><Users size={13} /> {numberFormat.format(session.contact_count)} contactos</span>
                    </> : <span>{session.can_migrate ? 'Selecciona para revisar sus datos' : 'No disponible'}</span>}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-theme bg-theme-base/45 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-xs leading-5 text-theme-muted">Asocia los chats de la copia de desarrollo al espacio de cada número. Esta acción no copia reglas, menús ni medios.</p>
          <button type="button" onClick={() => void handlePreview()} disabled={selectedIds.length === 0 || previewing || migrating || !!loadError} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50">
            {previewing ? <LoaderCircle size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {previewing ? 'Revisando datos…' : `Previsualizar ${selectedIds.length || ''} sesión${selectedIds.length === 1 ? '' : 'es'}`}
          </button>
        </div>
      </section>

      {preview && (
        <section className="overflow-hidden rounded-3xl border border-primary-500/30 bg-theme-card shadow-sm">
          <div className="border-b border-theme px-5 py-5 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-700 dark:text-primary-300">Paso 2 · revisión</p>
            <h4 className="mt-1 text-lg font-bold text-theme-main">Se asociarán mensajes y contactos al espacio de cada número</h4>
            <p className="mt-1 text-sm text-theme-muted">Los datos originales se conservan; la migración agrega el vínculo al espacio y se puede reintentar de forma segura.</p>
          </div>
          <div className="grid gap-3 p-5 sm:grid-cols-3 sm:px-6">
            <div className="rounded-2xl border border-theme bg-theme-base/60 p-4"><span className="text-xs font-semibold text-theme-muted">Números seleccionados</span><p className="mt-1 text-2xl font-bold tabular-nums text-theme-main">{preview.length}</p></div>
            <div className="rounded-2xl border border-theme bg-theme-base/60 p-4"><span className="text-xs font-semibold text-theme-muted">Mensajes asociados</span><p className="mt-1 text-2xl font-bold tabular-nums text-theme-main">{numberFormat.format(totalMessages)}</p></div>
            <div className="rounded-2xl border border-theme bg-theme-base/60 p-4"><span className="text-xs font-semibold text-theme-muted">Contactos vinculados</span><p className="mt-1 text-2xl font-bold tabular-nums text-theme-main">{numberFormat.format(totalContacts)}</p></div>
          </div>
          <div className="space-y-2 px-5 pb-5 sm:px-6">
            {preview.map((session) => (
              <div key={session.session_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-theme px-3.5 py-3">
                <span className="font-semibold text-theme-main">+{session.phone_number.replace(/^\+/, '')}</span>
                <span className="text-xs text-theme-muted">{numberFormat.format(session.message_count)} mensajes · {numberFormat.format(session.contact_count)} contactos</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3 border-t border-theme bg-primary-500/[0.04] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="max-w-xl text-xs leading-5 text-theme-muted">Confirma para crear o actualizar los espacios lógicos y asociar estos mensajes y contactos.</p>
            <button type="button" onClick={() => void handleMigrate()} disabled={migrating || preview.length === 0} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-primary-800 disabled:opacity-50">
              {migrating ? <LoaderCircle size={16} className="animate-spin" /> : <Database size={16} />}
              {migrating ? 'Migrando…' : `Migrar ${preview.length} sesión${preview.length === 1 ? '' : 'es'}`}
            </button>
          </div>
        </section>
      )}

      {results && (
        <section className="rounded-3xl border border-theme bg-theme-card p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-xl bg-theme-base p-2.5 text-theme-muted"><BadgeCheck size={20} /></div>
            <div><h4 className="font-bold text-theme-main">Resultado de la migración</h4><p className="text-sm text-theme-muted">Las sesiones fallidas se pueden seleccionar y reintentar.</p></div>
          </div>
          <div className="space-y-2">
            {results.map((result) => (
              <div key={result.session_id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-theme px-3.5 py-3">
                <div><p className="font-semibold text-theme-main">+{result.phone_number.replace(/^\+/, '')}</p><p className="mt-1 text-xs text-theme-muted">{result.success ? `${numberFormat.format(result.message_count)} mensajes · ${numberFormat.format(result.contact_count)} contactos` : result.error}</p></div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${result.success ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-200'}`}>{result.success ? 'Completada' : 'Requiere reintento'}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="px-1 text-xs leading-5 text-theme-muted">
        Los contactos de la cuenta pueden aparecer en más de un espacio si escribieron a varios números. Cada migración conserva el número de origen y no copia ni elimina conversaciones.
      </p>
    </div>
  );
};
